/**
 * 学习目标规划详情页
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StudyGoal,
  getSettings,
  saveSettings,
  defaultDb,
} from '../db/indexedDb';
import {
  DEFAULT_STUDY_GOAL,
  getCardCounts,
  getCompletedCounts,
  getTodayDueCount,
  calculateGoal,
  formatMinutes,
  formatDaysLeft,
  CardCounts,
  GoalCalculation,
  TodayDueInfo,
} from '../config/goalPlanner';
import './GoalPlannerPage.css';

export function GoalPlannerPage() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState<StudyGoal>(DEFAULT_STUDY_GOAL);
  const [cardCounts, setCardCounts] = useState<CardCounts | null>(null);
  const [completedCounts, setCompletedCounts] = useState<CardCounts | null>(null);
  const [todayDue, setTodayDue] = useState<TodayDueInfo>({ totalDue: 0, speakingDue: 0, writingDue: 0, dueMinutes: 0 });
  const [calculation, setCalculation] = useState<GoalCalculation | null>(null);
  const [saving, setSaving] = useState(false);

  // 加载数据
  useEffect(() => {
    (async () => {
      // 加载现有目标
      const settings = await getSettings('app', defaultDb);
      if (settings?.studyGoal) {
        // 兼容旧数据格式
        const savedGoal = settings.studyGoal;
        const migratedGoal: StudyGoal = {
          ...DEFAULT_STUDY_GOAL,
          ...savedGoal,
          reviewSettings: {
            includeReview: savedGoal.reviewSettings?.includeReview ?? true,
            speaking: savedGoal.reviewSettings?.speaking ?? {
              reviewTimes: (savedGoal.reviewSettings as any)?.reviewTimes ?? 2,
              reviewRatio: (savedGoal.reviewSettings as any)?.reviewRatio ?? 0.5,
            },
            writing: savedGoal.reviewSettings?.writing ?? {
              reviewTimes: (savedGoal.reviewSettings as any)?.reviewTimes ?? 4,
              reviewRatio: (savedGoal.reviewSettings as any)?.reviewRatio ?? 0.5,
            },
          },
        };
        setGoal(migratedGoal);
      }

      // 统计卡片数量
      const counts = await getCardCounts(defaultDb);
      setCardCounts(counts);
    })();
  }, []);

  // 当目标或卡片数量变化时重新计算
  useEffect(() => {
    if (!cardCounts) return;

    (async () => {
      const completed = await getCompletedCounts(goal, defaultDb);
      setCompletedCounts(completed);

      const due = await getTodayDueCount(goal, defaultDb);
      setTodayDue(due);

      const calc = calculateGoal(goal, cardCounts, completed, due);
      setCalculation(calc);
    })();
  }, [goal, cardCounts]);

  // 保存目标
  const handleSave = async () => {
    setSaving(true);
    try {
      const settings = await getSettings('app', defaultDb);
      await saveSettings({
        ...settings,
        id: 'app',
        studyGoal: { ...goal, updatedAt: Date.now() },
        dailyLimits: settings?.dailyLimits ?? {},
        createdAt: settings?.createdAt ?? Date.now(),
        updatedAt: Date.now(),
      }, defaultDb);
      navigate('/today');
    } catch (e) {
      alert('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 更新目标日期
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    date.setHours(23, 59, 59, 999);
    setGoal({ ...goal, targetDate: date.getTime() });
  };

  // 格式化日期为 input[type=date] 的值
  const formatDateValue = (ts: number) => {
    const d = new Date(ts);
    return d.toISOString().split('T')[0];
  };

  if (!cardCounts || !calculation) {
    return <div className="goal-page loading">加载中...</div>;
  }

  return (
    <div className="goal-page">
      <header className="goal-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← 返回</button>
        <h1>学习目标规划</h1>
      </header>

      <main className="goal-content">
        {/* 目标日期 */}
        <section className="goal-section">
          <h2>🎯 目标日期</h2>
          <div className="goal-card">
            <div className="date-row">
              <input
                type="date"
                value={formatDateValue(goal.targetDate)}
                onChange={handleDateChange}
                min={formatDateValue(Date.now() + 24 * 60 * 60 * 1000)}
              />
              <span className="days-left">还剩 {formatDaysLeft(goal.targetDate)}</span>
            </div>
          </div>
        </section>

        {/* 学习范围 */}
        <section className="goal-section">
          <h2>📚 学习范围</h2>

          {/* 口语 */}
          <div className="goal-card">
            <div className="scope-header">
              <label>
                <input
                  type="checkbox"
                  checked={goal.scope.speaking.enabled}
                  onChange={(e) => setGoal({
                    ...goal,
                    scope: { ...goal.scope, speaking: { ...goal.scope.speaking, enabled: e.target.checked } }
                  })}
                />
                <span>口语</span>
              </label>
              <span className="card-count">
                共 {cardCounts.speakingPart1 + cardCounts.speakingPart2 + cardCounts.speakingPart3} 张
              </span>
            </div>

            {goal.scope.speaking.enabled && (
              <div className="scope-detail">
                {/* Part 1 */}
                <div className="part-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={goal.scope.speaking.parts.indexOf('Part 1') >= 0}
                      onChange={(e) => {
                        const parts = e.target.checked
                          ? [...goal.scope.speaking.parts, 'Part 1' as const]
                          : goal.scope.speaking.parts.filter(p => p !== 'Part 1');
                        setGoal({ ...goal, scope: { ...goal.scope, speaking: { ...goal.scope.speaking, parts } } });
                      }}
                    />
                    Part 1
                  </label>
                  <span>{cardCounts.speakingPart1}张</span>
                  <span>×</span>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    value={goal.timeEstimates.speakingPart1}
                    onChange={(e) => setGoal({
                      ...goal,
                      timeEstimates: { ...goal.timeEstimates, speakingPart1: parseInt(e.target.value) || 3 }
                    })}
                  />
                  <span>分</span>
                </div>

                {/* Part 2 */}
                <div className="part-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={goal.scope.speaking.parts.indexOf('Part 2') >= 0}
                      onChange={(e) => {
                        const parts = e.target.checked
                          ? [...goal.scope.speaking.parts, 'Part 2' as const]
                          : goal.scope.speaking.parts.filter(p => p !== 'Part 2');
                        setGoal({ ...goal, scope: { ...goal.scope, speaking: { ...goal.scope.speaking, parts } } });
                      }}
                    />
                    Part 2
                  </label>
                  <span>{cardCounts.speakingPart2}张</span>
                  <span>×</span>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    value={goal.timeEstimates.speakingPart2}
                    onChange={(e) => setGoal({
                      ...goal,
                      timeEstimates: { ...goal.timeEstimates, speakingPart2: parseInt(e.target.value) || 7 }
                    })}
                  />
                  <span>分</span>
                </div>

                {/* Part 3 */}
                <div className="part-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={goal.scope.speaking.parts.indexOf('Part 3') >= 0}
                      onChange={(e) => {
                        const parts = e.target.checked
                          ? [...goal.scope.speaking.parts, 'Part 3' as const]
                          : goal.scope.speaking.parts.filter(p => p !== 'Part 3');
                        setGoal({ ...goal, scope: { ...goal.scope, speaking: { ...goal.scope.speaking, parts } } });
                      }}
                    />
                    Part 3
                  </label>
                  <span>{cardCounts.speakingPart3}张</span>
                  <span>×</span>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    value={goal.timeEstimates.speakingPart3}
                    onChange={(e) => setGoal({
                      ...goal,
                      timeEstimates: { ...goal.timeEstimates, speakingPart3: parseInt(e.target.value) || 3 }
                    })}
                  />
                  <span>分</span>
                </div>

                {/* Part 抽取比例 */}
                <div className="ratio-row">
                  <span>抽取比例:</span>
                  <span>P1</span>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={goal.speakingPartRatio.part1}
                    onChange={(e) => setGoal({
                      ...goal,
                      speakingPartRatio: { ...goal.speakingPartRatio, part1: parseInt(e.target.value) || 0 }
                    })}
                  />
                  <span>:</span>
                  <span>P2</span>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={goal.speakingPartRatio.part2}
                    onChange={(e) => setGoal({
                      ...goal,
                      speakingPartRatio: { ...goal.speakingPartRatio, part2: parseInt(e.target.value) || 0 }
                    })}
                  />
                  <span>:</span>
                  <span>P3</span>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={goal.speakingPartRatio.part3}
                    onChange={(e) => setGoal({
                      ...goal,
                      speakingPartRatio: { ...goal.speakingPartRatio, part3: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>

                {/* 学习比例 */}
                <div className="ratio-row">
                  <span>学习比例:</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={goal.scope.speaking.ratio ?? 100}
                    onChange={(e) => setGoal({
                      ...goal,
                      scope: { ...goal.scope, speaking: { ...goal.scope.speaking, ratio: parseInt(e.target.value) || 100 } }
                    })}
                  />
                  <span>%</span>
                  <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>
                    ≈ {Math.ceil((cardCounts.speakingPart1 + cardCounts.speakingPart2 + cardCounts.speakingPart3) * (goal.scope.speaking.ratio ?? 100) / 100)}张
                  </span>
                </div>

                <div className="subtotal">
                  首次学习: {formatMinutes(
                    calculation.cardsByType.speakingPart1 * goal.timeEstimates.speakingPart1 +
                    calculation.cardsByType.speakingPart2 * goal.timeEstimates.speakingPart2 +
                    calculation.cardsByType.speakingPart3 * goal.timeEstimates.speakingPart3
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 写作 */}
          <div className="goal-card">
            <div className="scope-header">
              <label>
                <input
                  type="checkbox"
                  checked={goal.scope.writing.enabled}
                  onChange={(e) => setGoal({
                    ...goal,
                    scope: { ...goal.scope, writing: { ...goal.scope.writing, enabled: e.target.checked } }
                  })}
                />
                <span>写作个人</span>
              </label>
              <span className="card-count">共 {cardCounts.writing} 张</span>
            </div>

            {goal.scope.writing.enabled && (
              <div className="scope-detail">
                <div className="part-row">
                  <span>{cardCounts.writing}张</span>
                  <span>×</span>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    value={goal.timeEstimates.writing}
                    onChange={(e) => setGoal({
                      ...goal,
                      timeEstimates: { ...goal.timeEstimates, writing: parseInt(e.target.value) || 4 }
                    })}
                  />
                  <span>分</span>
                </div>
                <div className="subtotal">
                  首次学习: {formatMinutes(cardCounts.writing * goal.timeEstimates.writing)}
                </div>
              </div>
            )}
          </div>

          {/* 听力和词汇 */}
          <div className="goal-card">
            <div className="scope-header">
              <label>
                <input
                  type="checkbox"
                  checked={goal.scope.listening.enabled}
                  onChange={(e) => setGoal({
                    ...goal,
                    scope: { ...goal.scope, listening: { enabled: e.target.checked } }
                  })}
                />
                <span>听力</span>
              </label>
              <label style={{ marginLeft: 24 }}>
                <input
                  type="checkbox"
                  checked={goal.scope.vocab.enabled}
                  onChange={(e) => setGoal({
                    ...goal,
                    scope: { ...goal.scope, vocab: { enabled: e.target.checked } }
                  })}
                />
                <span>词汇</span>
              </label>
            </div>
          </div>
        </section>

        {/* 复习设置 */}
        <section className="goal-section">
          <h2>🔄 复习设置</h2>
          <div className="goal-card">
            <label className="review-toggle">
              <input
                type="checkbox"
                checked={goal.reviewSettings.includeReview}
                onChange={(e) => setGoal({
                  ...goal,
                  reviewSettings: { ...goal.reviewSettings, includeReview: e.target.checked }
                })}
              />
              <span>计入复习时间</span>
            </label>

            {goal.reviewSettings.includeReview && (
              <div className="review-detail">
                {/* 口语复习设置 */}
                <div className="review-row">
                  <span style={{ fontWeight: 700 }}>口语:</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={goal.reviewSettings.speaking.reviewTimes}
                    onChange={(e) => setGoal({
                      ...goal,
                      reviewSettings: { 
                        ...goal.reviewSettings, 
                        speaking: { ...goal.reviewSettings.speaking, reviewTimes: parseInt(e.target.value) || 2 }
                      }
                    })}
                  />
                  <span>次</span>
                  <span>×</span>
                  <input
                    type="number"
                    min="10"
                    max="100"
                    value={Math.round(goal.reviewSettings.speaking.reviewRatio * 100)}
                    onChange={(e) => setGoal({
                      ...goal,
                      reviewSettings: { 
                        ...goal.reviewSettings, 
                        speaking: { ...goal.reviewSettings.speaking, reviewRatio: (parseInt(e.target.value) || 50) / 100 }
                      }
                    })}
                  />
                  <span>%</span>
                </div>

                {/* 写作复习设置 */}
                <div className="review-row">
                  <span style={{ fontWeight: 700 }}>写作:</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={goal.reviewSettings.writing.reviewTimes}
                    onChange={(e) => setGoal({
                      ...goal,
                      reviewSettings: { 
                        ...goal.reviewSettings, 
                        writing: { ...goal.reviewSettings.writing, reviewTimes: parseInt(e.target.value) || 4 }
                      }
                    })}
                  />
                  <span>次</span>
                  <span>×</span>
                  <input
                    type="number"
                    min="10"
                    max="100"
                    value={Math.round(goal.reviewSettings.writing.reviewRatio * 100)}
                    onChange={(e) => setGoal({
                      ...goal,
                      reviewSettings: { 
                        ...goal.reviewSettings, 
                        writing: { ...goal.reviewSettings.writing, reviewRatio: (parseInt(e.target.value) || 50) / 100 }
                      }
                    })}
                  />
                  <span>%</span>
                </div>

                <div className="subtotal">
                  预计复习: {formatMinutes(calculation.reviewMinutes)}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 计算结果 */}
        <section className="goal-section">
          <h2>📊 计算结果</h2>
          <div className="goal-card result-card">
            <table className="result-table">
              <thead>
                <tr>
                  <th></th>
                  <th>首次</th>
                  <th>复习</th>
                  <th>总计</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>时间</td>
                  <td>{formatMinutes(calculation.firstPassMinutes)}</td>
                  <td>{formatMinutes(calculation.reviewMinutes)}</td>
                  <td><strong>{formatMinutes(calculation.totalMinutes)}</strong></td>
                </tr>
                <tr>
                  <td>卡片</td>
                  <td>{calculation.totalCards}张</td>
                  <td>-</td>
                  <td><strong>{calculation.totalCards}张</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 每日任务 */}
        <section className="goal-section">
          <h2>⚙️ 每日任务（今日）</h2>
          <div className="goal-card daily-card">
            <table className="result-table">
              <thead>
                <tr>
                  <th></th>
                  <th>新卡片</th>
                  <th>复习</th>
                  <th>总计</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>卡片</td>
                  <td>{calculation.dailyNewCards}张</td>
                  <td>{calculation.dailyDueCards}张</td>
                  <td><strong>{calculation.dailyCards}张</strong></td>
                </tr>
                <tr>
                  <td>时间</td>
                  <td>{formatMinutes(calculation.dailyNewMinutes)}</td>
                  <td>{formatMinutes(calculation.dailyDueMinutes)}</td>
                  <td><strong>{formatMinutes(calculation.dailyMinutes)}</strong></td>
                </tr>
              </tbody>
            </table>
            {calculation.dailyMinutes > 120 && (
              <div className="daily-warning">
                ⚠️ 每天超过2小时，建议延长目标日期或减少范围
              </div>
            )}
          </div>
        </section>

        {/* 保存按钮 */}
        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? '保存中...' : '保存目标'}
        </button>
      </main>
    </div>
  );
}

export default GoalPlannerPage;
